import { FieldValidator } from 'final-form';
import { declOfNum } from './Helper';

export const composeValidators =
  (...validators: FieldValidator<string | number>[]) =>
  (value: number | string): string | undefined =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined
    );

export const validateTel = (value: string | number | undefined) => {
  const tel = /^[0-9]{10}$/;
  if (!value) {
    return 'Обязательно для заполнения';
  }
  if (!tel.test(value.toString().replace(/[^0-9]+/g, ''))) {
    return 'Некорректный номер телефона';
  }
  return undefined;
};

export const validateRequired = (
  value: string | number
): string | undefined => {
  if (!value) {
    return 'Обязательно для заполнения';
  }
  return undefined;
};

export const validateNumber = (value: string | number) => {
  const number = /^[0-9]+$/;
  if (value && !number.test(value.toString())) {
    return 'только цифры';
  }
  return undefined;
};

export const validateInn = (
  valueToCheck: string | number | undefined = ''
): string | undefined => {
  let result;
  if (!valueToCheck) {
    return undefined;
  }

  const value = valueToCheck.toString();

  if (/[^0-9]/.test(value)) {
    result = 'ИНН может состоять только из цифр';
  } else if ([10, 12].indexOf(value.length) === -1) {
    result = 'ИНН может состоять только из 10 или 12 цифр';
  }

  const checkDigit = (v: string, coefficients: number[]) => {
    const n = coefficients.reduce((sum, c, i) => sum + c * +v[i], 0);
    return +((n % 11) % 10);
  };

  let checkCoefficients = false;

  if (value.length === 10) {
    const n10 = checkDigit(value, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
    if (n10 === +value[9]) {
      checkCoefficients = true;
    }
  } else if (value.length === 12) {
    const n11 = checkDigit(value, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
    const n12 = checkDigit(value, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
    if (n11 === +value[10] && n12 === +value[11]) {
      checkCoefficients = true;
    }
  }

  if (!checkCoefficients) {
    result = 'Некорректный ИНН';
  }
  return result;
};

export const validateOnlyCyrillic = (value: string | undefined) => {
  const onlyLetters = /^[А-яЁё .-]+$/;
  if (value && !onlyLetters.test(value)) {
    return 'Неверный формат — заполните на русском языке';
  }
  return undefined;
};

export const parseNumber = (value: string | number): string =>
  `${value}`.replace(/[^0-9]+/g, '');

export const parseNumberLength =
  (length: number | string) =>
  (value: string | number): string =>
    `${value}`.replace(/[^0-9]+/g, '').substring(0, +length);

export const validateMaxLength =
  (maxLength: number) => (value: string | number | undefined) => {
    let result;
    if (value && value.toString().length > maxLength) {
      result = `Максимальная длина ${maxLength} ${declOfNum(maxLength, [
        'символ',
        'символа',
        'символов',
      ])}`;
    }
    return result;
  };

export const validateMinLength =
  (minLength: number) =>
  (value: string | number | undefined): string | undefined => {
    let result;
    const stringValue = value?.toString() || '';
    if (stringValue && stringValue.length < minLength) {
      result = `Минимальная длина ${minLength} ${declOfNum(minLength, [
        'символ',
        'символа',
        'символов',
      ])}`;
    }
    return result;
  };

export const validateExactLength =
  (length: number) => (value: string | number) => {
    if (value && value.toString().length !== length) {
      return `Введите ${length} ${declOfNum(length, [
        'символ',
        'символа',
        'символов',
      ])}`;
    }
    return undefined;
  };

export const validateName = (value: string | number | undefined) => {
  const onlyLetters = /^[А-яЁё .-]+$/;
  if (value && !onlyLetters.test(value.toString())) {
    return 'Неверный формат - допустимы только русские буквы';
  }
  return undefined;
};

export const validateEmail = (value: string | number | undefined) => {
  const email =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!value) {
    return 'Обязательно для заполнения';
  }
  if (!email.test(value.toString())) {
    return 'Некорректный e-mail адрес';
  }
  return undefined;
};

export const validateSnils = (snils: string | number | undefined) => {
  let result;
  const snilsString = `${snils}`;

  if (!snils) {
    return undefined;
  }

  if (/[^0-9]/.test(snilsString)) {
    result = 'СНИЛС может состоять только из цифр';
  } else if (snilsString.length !== 11) {
    result = 'СНИЛС может состоять только из 11 цифр';
  } else {
    let sum = 0;
    for (let i = 0; i < 9; i += 1) {
      sum += +snilsString[i] * (9 - i);
    }
    let checkDigit = 0;
    if (sum < 100) {
      checkDigit = sum;
    } else if (sum > 101) {
      checkDigit = parseInt((sum % 101).toString(), 10);
      if (checkDigit === 100) {
        checkDigit = 0;
      }
    }
    if (checkDigit !== parseInt(snils.toString().slice(-2), 10)) {
      result = 'Некорректный СНИЛС';
    }
  }
  return result;
};

export const validatePassport = (
  value: string | number | undefined
): string | undefined => {
  const passportSeries = /^[0-9]{10}$/;
  const v = `${value}`.replace(/\s+/g, '');
  const valid = passportSeries.test(v);
  if (value && !valid) {
    return '10 знаков, только цифры';
  }
  return undefined;
};

export const validateBankAccount = (value: string | number | undefined) => {
  const passportSeries = /^[0-9]{20}$/;
  if (value && !passportSeries.test(value.toString())) {
    return '20 знаков, только цифры';
  }
  return undefined;
};
